/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import commonStyles from '../../styles/commonStyles';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Collapse,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExperimentOutlined,
  ThunderboltOutlined,
  CloudOutlined,
  BulbOutlined,
  WarningOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import MainLayout from '../../components/common/MainLayout';
import {
  useListGrowthProfilesQuery,
  useCreateGrowthProfileMutation,
  useUpdateGrowthProfileMutation,
  useDeleteGrowthProfileMutation,
} from '../../api/simulationApi';
import type { GrowthProfile, GrowthProfileParams } from '../../types/simulation';

type LightRequirement = NonNullable<NonNullable<GrowthProfileParams['annualGrowthModifiers']>['lightRequirement']>;
type LightPhaseTarget = NonNullable<NonNullable<LightRequirement['phaseTargets']>[string]>;

const { Title, Paragraph, Text } = Typography;

const styles = {
  titleNoMargin: css({ margin: 0 }),
  paragraphNoMargin: css({ margin: 0 }),
  headerRow: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }),
  cardBody: css({ padding: '12px 0' }),
  section: css({
    border: '1px solid #f0f0f0',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
  }),
  sectionTitle: css({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
    fontSize: 14,
    fontWeight: 600,
  }),
  statRow: css({
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 16,
  }),
  statCard: css({
    background: '#fafafa',
    borderRadius: 6,
    padding: '8px 14px',
    minWidth: 120,
  }),
  profileCard: css({
    borderLeft: '4px solid #52c41a',
    '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  }),
  cardActions: css({ display: 'flex', gap: 4 }),
  speciesTag: css({ fontSize: 13 }),
  gompertzRow: css({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 12,
  }),
  formSection: css({
    background: '#fafafa',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  }),
  selectWidth220: css({ width: 220 }),
  searchMaxWidth: css({ maxWidth: 320 }),
};

const PLANT_TYPE_LABELS: Record<string, string> = {
  annual: 'Однолетнее',
  biennial: 'Двулетнее',
  perennial: 'Многолетнее',
};

const PLANT_TYPE_COLORS: Record<string, string> = {
  annual: 'lime',
  biennial: 'gold',
  perennial: 'green',
};

const CLIMATE_ZONE_LABELS: Record<string, string> = {
  temperate_continental: 'Умеренно-континентальный',
  continental: 'Континентальный',
  subarctic: 'Субарктический',
  temperate: 'Умеренный',
  subtropical: 'Субтропический',
};
const LIGHT_PHASE_LABELS: Record<string, string> = {
  germination: 'Прорастание',
  seedling: 'Рассада',
  vegetative: 'Вегетация',
  flowering: 'Цветение',
  fruiting: 'Плодоношение',
  senescence: 'Старение',
};

const ANNUAL_LIGHT_PHASES = ['germination', 'seedling', 'vegetative', 'flowering', 'fruiting'] as const;

function hasNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function hasObjectValues(value: Record<string, unknown> | undefined): boolean {
  return Boolean(value) && Object.values(value as Record<string, unknown>).some((entry) => entry != null);
}

function normalizePhaseTargets(
  phaseTargets?: LightRequirement['phaseTargets'],
): LightRequirement['phaseTargets'] | undefined {
  if (!phaseTargets) return undefined;

  const normalizedEntries = Object.entries(phaseTargets)
    .map(([phase, target]) => {
      if (!target) return null;
      const normalizedTarget = {
        ...(hasNumber(target.dliMinMol) ? { dliMinMol: target.dliMinMol } : {}),
        ...(hasNumber(target.dliTargetMol) ? { dliTargetMol: target.dliTargetMol } : {}),
        ...(hasNumber(target.photoperiodTargetHours) ? { photoperiodTargetHours: target.photoperiodTargetHours } : {}),
      };
      return hasObjectValues(normalizedTarget) ? [phase, normalizedTarget] : null;
    })
    .filter((entry): entry is [string, LightPhaseTarget] => Boolean(entry));

  return normalizedEntries.length ? Object.fromEntries(normalizedEntries) : undefined;
}

function normalizeLightRequirement(lightRequirement?: LightRequirement): LightRequirement | undefined {
  if (!lightRequirement) return undefined;

  const normalized = {
    ...(hasNumber(lightRequirement.dliMinMol) ? { dliMinMol: lightRequirement.dliMinMol } : {}),
    ...(hasNumber(lightRequirement.dliTargetMol) ? { dliTargetMol: lightRequirement.dliTargetMol } : {}),
    ...(hasNumber(lightRequirement.photoperiodTargetHours)
      ? { photoperiodTargetHours: lightRequirement.photoperiodTargetHours }
      : {}),
    ...(hasNumber(lightRequirement.photoperiodMinHours)
      ? { photoperiodMinHours: lightRequirement.photoperiodMinHours }
      : {}),
    ...(hasNumber(lightRequirement.photoperiodMaxHours)
      ? { photoperiodMaxHours: lightRequirement.photoperiodMaxHours }
      : {}),
    ...(hasNumber(lightRequirement.lampPpfdUmol)
      ? { lampPpfdUmol: lightRequirement.lampPpfdUmol }
      : {}),
    ...(hasNumber(lightRequirement.lampEfficacyUmolJ)
      ? { lampEfficacyUmolJ: lightRequirement.lampEfficacyUmolJ }
      : {}),
    ...(normalizePhaseTargets(lightRequirement.phaseTargets)
      ? { phaseTargets: normalizePhaseTargets(lightRequirement.phaseTargets) }
      : {}),
  };

  return hasObjectValues(normalized) ? normalized : undefined;
}

function formatLightTarget(target?: {
  dliMinMol?: number;
  dliTargetMol?: number;
  photoperiodTargetHours?: number;
}) {
  if (!target) return null;

  const dli = [
    hasNumber(target.dliMinMol) ? `мин ${target.dliMinMol}` : null,
    hasNumber(target.dliTargetMol) ? `цель ${target.dliTargetMol}` : null,
  ].filter(Boolean).join(' / ');

  const photoperiod = hasNumber(target.photoperiodTargetHours)
    ? `${target.photoperiodTargetHours} ч`
    : null;

  return [dli ? `${dli} mol/m2/day` : null, photoperiod ? `фотопериод ${photoperiod}` : null]
    .filter(Boolean)
    .join(' · ');
}

/** Helper: format Gompertz dimension into human-readable card */
function GompertzCard({ label, dim }: { label: string; dim?: { maxCm: number; k: number; inflectionYears: number } }) {
  if (!dim) return null;
  const maxM = dim.maxCm >= 100 ? `${(dim.maxCm / 100).toFixed(1)} м` : `${dim.maxCm} см`;
  return (
    <div css={styles.statCard}>
      <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{label}</Text>
      <Text strong style={{ fontSize: 16 }}>{maxM}</Text>
      <br />
      <Tooltip title={`k=${dim.k}, перегиб на ${dim.inflectionYears} году`}>
        <Text type="secondary" style={{ fontSize: 11 }}>
          скорость: {dim.k} · перегиб: {dim.inflectionYears} лет
        </Text>
      </Tooltip>
    </div>
  );
}

/** Section: Growth model (Gompertz curves) */
function GrowthModelSection({ params }: { params: GrowthProfileParams }) {
  return (
    <div css={styles.section}>
      <div css={styles.sectionTitle}>
        <ExperimentOutlined style={{ color: '#1890ff' }} />
        <span>Модель роста (Гомпертц)</span>
      </div>
      <div css={styles.gompertzRow}>
        <GompertzCard label="Высота макс." dim={params.height} />
        <GompertzCard label="Диам. ствола макс." dim={params.trunkDiameter} />
        <GompertzCard label="Диам. кроны макс." dim={params.crownDiameter} />
      </div>
    </div>
  );
}

/** Section: Optimal conditions (temperature, water, light) */
function OptimalConditionsSection({ params }: { params: GrowthProfileParams }) {
  const mods = params.annualGrowthModifiers;
  if (!mods) return null;

  return (
    <div css={styles.section}>
      <div css={styles.sectionTitle}>
        <ThunderboltOutlined style={{ color: '#fa8c16' }} />
        <span>Оптимальные условия</span>
      </div>
      <Row gutter={[16, 12]}>
        {mods.tempStress && (
          <Col xs={24} sm={12} md={8}>
            <Card size="small" title={<><CloudOutlined /> Температура</>}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Оптимум">
                  {mods.tempStress.optMinC}°C … {mods.tempStress.optMaxC}°C
                </Descriptions.Item>
                <Descriptions.Item label="Летальные">
                  {mods.tempStress.minC}°C … {mods.tempStress.maxC}°C
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        )}
        {mods.waterStress && (
          <Col xs={24} sm={12} md={8}>
            <Card size="small" title={<><CloudOutlined /> Влага</>}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Порог стресса">
                  {(mods.waterStress.threshold * 100).toFixed(0)}% влажности
                </Descriptions.Item>
                <Descriptions.Item label="Снижение роста">
                  ×{mods.waterStress.reductionFactor}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        )}
        {mods.lightRequirement && (
          <Col xs={24} sm={12} md={8}>
            <Card size="small" title={<><BulbOutlined /> Освещение</>}>
              <Descriptions column={1} size="small">
                {(hasNumber(mods.lightRequirement.dliMinMol) || hasNumber(mods.lightRequirement.dliTargetMol)) && (
                  <Descriptions.Item label="Базовый DLI">
                    {formatLightTarget(mods.lightRequirement) ?? 'не задан'}
                  </Descriptions.Item>
                )}
                {hasNumber(mods.lightRequirement.photoperiodTargetHours) && !hasNumber(mods.lightRequirement.dliTargetMol) && (
                  <Descriptions.Item label="Фотопериод">
                    {mods.lightRequirement.photoperiodTargetHours} ч/сутки
                  </Descriptions.Item>
                )}
                {mods.lightRequirement.phaseTargets && Object.keys(mods.lightRequirement.phaseTargets).length > 0 && (
                  <Descriptions.Item label="Фазы">
                    <Space direction="vertical" size={2}>
                      {Object.entries(mods.lightRequirement.phaseTargets).map(([phase, target]) => (
                        <Text key={phase} style={{ fontSize: 12 }}>
                          <Text strong>{LIGHT_PHASE_LABELS[phase] ?? phase}:</Text> {formatLightTarget(target) ?? 'не задано'}
                        </Text>
                      ))}
                    </Space>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
}

/** Section: Care requirements */
function CareSection({ params }: { params: GrowthProfileParams }) {
  const care = params.careTargets;
  if (!care) return null;
  const watering = care.watering;
  const fert = care.fertilizing;

  return (
    <div css={styles.section}>
      <div css={styles.sectionTitle}>
        <ExperimentOutlined style={{ color: '#52c41a' }} />
        <span>Целевые коридоры ухода</span>
      </div>
      <Descriptions size="small" column={2}>
        {watering && (
          <Descriptions.Item label="Влажность">
            триггер {watering.triggerPct}% → цель {watering.targetPct}% · макс {watering.maxPct}%
          </Descriptions.Item>
        )}
        {fert?.targets && (
          <Descriptions.Item label="NPK">
            N {fert.targets.nitrogen?.trigger}/{fert.targets.nitrogen?.target} · P {fert.targets.phosphorus?.trigger}/{fert.targets.phosphorus?.target} · K {fert.targets.potassium?.trigger}/{fert.targets.potassium?.target}
          </Descriptions.Item>
        )}
        {fert?.pH && (
          <Descriptions.Item label="pH">
            {fert.pH.min}–{fert.pH.max}, цель {fert.pH.optimal}
          </Descriptions.Item>
        )}
      </Descriptions>
    </div>
  );
}

/** Section: Harvest model for annual/vegetable crops */
function HarvestSection({ params }: { params: GrowthProfileParams }) {
  const yieldModel = params.yieldModel;
  if (!yieldModel) return null;

  return (
    <div css={styles.section}>
      <div css={styles.sectionTitle}>
        <InboxOutlined style={{ color: '#d46b08' }} />
        <span>Модель урожая</span>
      </div>
      <div css={styles.statRow}>
        {yieldModel.expectedYieldPerPlantKg != null && (
          <Statistic
            title="Сезонная валовая урожайность"
            value={yieldModel.expectedYieldPerPlantKg}
            precision={2}
            suffix="кг/раст"
            valueStyle={{ fontSize: 20, color: '#52c41a' }}
          />
        )}
        {yieldModel.marketableFraction != null && (
          <Statistic
            title="Товарная доля"
            value={yieldModel.marketableFraction * 100}
            precision={0}
            suffix="%"
            valueStyle={{ fontSize: 20, color: '#13c2c2' }}
          />
        )}
        {yieldModel.peakFruitingEdu != null && (
          <Statistic
            title="Пик плодоношения"
            value={yieldModel.peakFruitingEdu}
            suffix="EDU"
            valueStyle={{ fontSize: 20, color: '#722ed1' }}
          />
        )}
      </div>
    </div>
  );
}

/** Section: Phenology stages (GDD) */
function PhenologySection({ params }: { params: GrowthProfileParams }) {
  const phen = params.phenology;
  if (!phen) return null;

  const stages = [
    { label: 'Конец покоя', gdd: phen.dormancyEndGDD, color: '#8c8c8c' },
    { label: 'Набухание почек', gdd: phen.budSwellingGDD, color: '#faad14' },
    { label: 'Активный рост', gdd: phen.activeGrowthGDD, color: '#52c41a' },
    { label: 'Подготовка к зиме', gdd: phen.winterPrepGDD, color: '#1890ff' },
  ];

  return (
    <div css={styles.section}>
      <div css={styles.sectionTitle}>
        <CalendarOutlined style={{ color: '#722ed1' }} />
        <span>Фенология (GDD ≥10°C)</span>
      </div>
      <div css={styles.statRow}>
        {stages.map((s) => (
          <div key={s.label} css={styles.statCard}>
            <Text type="secondary" style={{ fontSize: 11 }}>{s.label}</Text>
            <br />
            <Text strong style={{ fontSize: 16, color: s.color }}>{s.gdd}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}> GDD</Text>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Section: Mortality thresholds */
function MortalitySection({ params }: { params: GrowthProfileParams }) {
  const mort = params.mortality;
  if (!mort) return null;

  return (
    <div css={styles.section}>
      <div css={styles.sectionTitle}>
        <WarningOutlined style={{ color: '#ff4d4f' }} />
        <span>Пороги смертности</span>
      </div>
      <div css={styles.statRow}>
        <Statistic
          title="Засуха (дней)"
          value={mort.droughtDaysLethal}
          suffix="дн."
          valueStyle={{ fontSize: 20, color: '#ff4d4f' }}
        />
        <Statistic
          title="Мороз (летальный)"
          value={mort.frostLethalC}
          suffix="°C"
          valueStyle={{ fontSize: 20, color: '#1890ff' }}
        />
        <Statistic
          title="Здоровье (мин)"
          value={mort.healthScoreLethal}
          suffix="/100"
          valueStyle={{ fontSize: 20, color: '#faad14' }}
        />
      </div>
    </div>
  );
}

/** A single cultivation card */
function CultivationCard({
  profile,
  onEdit,
  onDelete,
}: {
  profile: GrowthProfile;
  onEdit: (p: GrowthProfile) => void;
  onDelete: (p: GrowthProfile) => void;
}) {
  const displayName = profile.cultivarName
    ? `${profile.speciesName ?? ''} — ${profile.cultivarName}`
    : profile.speciesName ?? 'Общий профиль';
  const latinName = profile.speciesLatin ?? '';
  const pt = profile.plantType ?? 'perennial';

  return (
    <Card
      css={styles.profileCard}
      style={{ borderLeftColor: PLANT_TYPE_COLORS[pt] === 'green' ? '#52c41a' : pt === 'annual' ? '#a0d911' : '#faad14' }}
    >
      <Collapse
        items={[{
          key: profile.id,
          label: (
            <div css={styles.headerRow}>
              <Space direction="vertical" size={0} style={{ flex: 1 }}>
                <Space wrap>
                  <Text strong style={{ fontSize: 16 }}>{profile.profileName}</Text>
                  <Tag color={PLANT_TYPE_COLORS[pt]}>{PLANT_TYPE_LABELS[pt]}</Tag>
                  {!profile.isDefault && <Tag color="orange">Неактивен</Tag>}
                </Space>
                <Space size={8}>
                  <Text type="secondary" style={{ fontStyle: 'italic' }}>{latinName}</Text>
                  {displayName !== profile.profileName && (
                    <Text type="secondary">({displayName})</Text>
                  )}
                </Space>
                {profile.climateZone && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Зона: {CLIMATE_ZONE_LABELS[profile.climateZone] ?? profile.climateZone}
                  </Text>
                )}
              </Space>
              <span css={styles.cardActions} onClick={(e) => e.stopPropagation()}>
                <Button
                  size="small"
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => onEdit(profile)}
                />
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => onDelete(profile)}
                />
              </span>
            </div>
          ),
          children: (
            <Space direction="vertical" size={0} css={commonStyles.fullWidth}>
              {profile.source && (
                <Alert
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                  message={<Text type="secondary" style={{ fontSize: 12 }}>Источник: {profile.source}</Text>}
                  style={{ marginBottom: 12 }}
                />
              )}
              <GrowthModelSection params={profile.params} />
              <OptimalConditionsSection params={profile.params} />
              <CareSection params={profile.params} />
              <HarvestSection params={profile.params} />
              <PhenologySection params={profile.params} />
              <MortalitySection params={profile.params} />
            </Space>
          ),
        }]}
      />
    </Card>
  );
}

// ───── Main Page Component ─────

const AdminCultivationCardsPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [plantTypeFilter, setPlantTypeFilter] = useState<string>('all');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<GrowthProfile | null>(null);
  const [form] = Form.useForm();
  const selectedPlantType = Form.useWatch('plantType', form) as GrowthProfile['plantType'] | undefined;

  const { data: profiles = [], isFetching: loading, isError } = useListGrowthProfilesQuery({});
  const [createProfile, { isLoading: creating }] = useCreateGrowthProfileMutation();
  const [updateProfile, { isLoading: updating }] = useUpdateGrowthProfileMutation();
  const [deleteProfile] = useDeleteGrowthProfileMutation();

  const filtered = useMemo(() => {
    let list = profiles;
    if (plantTypeFilter !== 'all') {
      list = list.filter((p) => (p.plantType ?? 'perennial') === plantTypeFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((p) =>
        p.profileName.toLowerCase().includes(q) ||
        (p.speciesName ?? '').toLowerCase().includes(q) ||
        (p.speciesLatin ?? '').toLowerCase().includes(q) ||
        (p.cultivarName ?? '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [profiles, plantTypeFilter, search]);

  const openCreate = useCallback(() => {
    form.resetFields();
    form.setFieldsValue({
      plantType: 'perennial',
      height: { maxCm: 2000, k: 0.035, inflectionYears: 30 },
      trunkDiameter: { maxCm: 60, k: 0.03, inflectionYears: 35 },
      crownDiameter: { maxCm: 600, k: 0.03, inflectionYears: 25 },
      careTargets: {
        watering: { scope: 'bed', triggerPct: 45, targetPct: 60, maxPct: 80 },
        fertilizing: {
          scope: 'bed',
          targets: {
            nitrogen: { trigger: 40, target: 70 },
            phosphorus: { trigger: 30, target: 55 },
            potassium: { trigger: 35, target: 60 },
          },
          pH: { min: 5.5, optimal: 6.5, max: 7.5 },
          formulaType: 'balanced',
        },
      },
      yieldModel: {
        expectedYieldPerPlantKg: 1.5,
        marketableFraction: 0.85,
        rampUpEdu: 80,
        peakFruitingEdu: 420,
        declineRatePctPer100Edu: 12,
        maxFruitingEdu: 1100,
        senescenceTailEdu: 120,
      },
      mortality: { droughtDaysLethal: 45, frostLethalC: -40, healthScoreLethal: 10 },
    });
    setCreateModalOpen(true);
  }, [form]);

  const openEdit = useCallback((profile: GrowthProfile) => {
    setEditingProfile(profile);
    const p = profile.params;
    form.setFieldsValue({
      profileName: profile.profileName,
      speciesId: profile.speciesId,
      cultivarId: profile.cultivarId,
      climateZone: profile.climateZone,
      plantType: profile.plantType ?? 'perennial',
      source: profile.source,
      height: p.height,
      trunkDiameter: p.trunkDiameter,
      crownDiameter: p.crownDiameter,
      tempStress: p.annualGrowthModifiers?.tempStress,
      waterStress: p.annualGrowthModifiers?.waterStress,
      lightRequirement: normalizeLightRequirement(p.annualGrowthModifiers?.lightRequirement),
      careTargets: p.careTargets,
      yieldModel: p.yieldModel,
      phenology: p.phenology,
      mortality: p.mortality,
    });
  }, [form]);

  const buildParams = (values: any): GrowthProfileParams => ({
    height: values.height,
    trunkDiameter: values.trunkDiameter,
    crownDiameter: values.crownDiameter,
    annualGrowthModifiers: {
      ...(values.tempStress ? { tempStress: values.tempStress } : {}),
      ...(values.waterStress ? { waterStress: values.waterStress } : {}),
      ...(normalizeLightRequirement(values.lightRequirement)
        ? { lightRequirement: normalizeLightRequirement(values.lightRequirement) }
        : {}),
    },
    careTargets: values.careTargets,
    yieldModel: values.yieldModel,
    phenology: values.phenology,
    mortality: values.mortality,
  });

  const handleCreate = useCallback(async () => {
    try {
      const values = await form.validateFields();
      await createProfile({
        speciesId: values.speciesId,
        cultivarId: values.cultivarId || null,
        profileName: values.profileName,
        climateZone: values.climateZone,
        params: buildParams(values),
        isDefault: true,
      } as any).unwrap();
      message.success('Техкарта создана');
      setCreateModalOpen(false);
    } catch (err: any) {
      if (err?.data?.message) message.error(err.data.message);
    }
  }, [form, createProfile]);

  const handleEdit = useCallback(async () => {
    if (!editingProfile) return;
    try {
      const values = await form.validateFields();
      await updateProfile({
        id: editingProfile.id,
        body: {
          profileName: values.profileName,
          climateZone: values.climateZone,
          params: buildParams(values),
        } as any,
      }).unwrap();
      message.success('Техкарта обновлена');
      setEditingProfile(null);
    } catch (err: any) {
      if (err?.data?.message) message.error(err.data.message);
    }
  }, [editingProfile, form, updateProfile]);

  const handleDelete = useCallback((profile: GrowthProfile) => {
    Modal.confirm({
      title: `Удалить техкарту «${profile.profileName}»?`,
      content: 'Это действие нельзя отменить. Симуляции, использующие этот профиль, переключатся на общий.',
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await deleteProfile(profile.id).unwrap();
          message.success('Техкарта удалена');
        } catch (err: any) {
          if (err?.data?.message) message.error(err.data.message);
        }
      },
    });
  }, [deleteProfile]);

  const renderFormContent = () => (
    <Form form={form} layout="vertical" size="small">
      <Form.Item name="profileName" label="Название" rules={[{ required: true }]}>
        <Input placeholder="Ель колючая — Подмосковье" />
      </Form.Item>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="speciesId" label="ID вида">
            <InputNumber style={{ width: '100%' }} placeholder="99" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="cultivarId" label="ID сорта">
            <InputNumber style={{ width: '100%' }} placeholder="(опционально)" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="plantType" label="Тип растения">
            <Select options={Object.entries(PLANT_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }))} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="climateZone" label="Климатическая зона">
            <Select
              allowClear
              placeholder="Выберите"
              options={Object.entries(CLIMATE_ZONE_LABELS).map(([v, l]) => ({ value: v, label: l }))}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="source" label="Источник данных">
            <Input placeholder="справочник дендрологии, Моск. обл." />
          </Form.Item>
        </Col>
      </Row>

      {/* Growth model */}
      <div css={styles.formSection}>
        <Title level={5} style={{ margin: '0 0 12px' }}>Модель роста (Гомпертц)</Title>
        {(['height', 'trunkDiameter', 'crownDiameter'] as const).map((dim) => (
          <Row gutter={16} key={dim}>
            <Col span={3}>
              <Text type="secondary">{dim === 'height' ? 'Высота' : dim === 'trunkDiameter' ? 'Ствол' : 'Крона'}</Text>
            </Col>
            <Col span={7}>
              <Form.Item name={[dim, 'maxCm']} label="Макс (см)" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item name={[dim, 'k']} label="k (скорость)" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0.001} step={0.005} />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item name={[dim, 'inflectionYears']} label="Перегиб (лет)" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0.1} />
              </Form.Item>
            </Col>
          </Row>
        ))}
      </div>

      {/* Optimal conditions */}
      <div css={styles.formSection}>
        <Title level={5} style={{ margin: '0 0 12px' }}>Оптимальные условия</Title>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name={['tempStress', 'optMinC']} label="Темп. опт. мин (°C)">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name={['tempStress', 'optMaxC']} label="Темп. опт. макс (°C)">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name={['tempStress', 'minC']} label="Темп. мин. (°C)">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name={['tempStress', 'maxC']} label="Темп. макс. (°C)">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name={['waterStress', 'threshold']} label="Порог влаги (0-1)">
              <InputNumber style={{ width: '100%' }} min={0} max={1} step={0.05} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name={['waterStress', 'reductionFactor']} label="Снижение роста (0-1)">
              <InputNumber style={{ width: '100%' }} min={0} max={1} step={0.1} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name={['lightRequirement', 'dliMinMol']} label="DLI мин. (mol/m2/day)">
              <InputNumber style={{ width: '100%' }} min={0} step={0.5} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name={['lightRequirement', 'dliTargetMol']} label="DLI цель (mol/m2/day)">
              <InputNumber style={{ width: '100%' }} min={0} step={0.5} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name={['lightRequirement', 'photoperiodTargetHours']} label="Фотопериод цель (ч)">
              <InputNumber style={{ width: '100%' }} min={0} max={24} step={0.5} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name={['lightRequirement', 'lampPpfdUmol']} label="PPFD лампы (µmol/m2/s)">
              <InputNumber style={{ width: '100%' }} min={0} step={10} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name={['lightRequirement', 'lampEfficacyUmolJ']} label="Эффективность лампы (µmol/J)">
              <InputNumber style={{ width: '100%' }} min={0} step={0.1} />
            </Form.Item>
          </Col>
          <Col span={18}>
            <Alert
              type="info"
              showIcon
              message="Для профилей света используются только DLI, фотопериод и параметры досветки. Lux-поля удалены из контракта."
            />
          </Col>
        </Row>
        {(selectedPlantType === 'annual' || selectedPlantType === 'biennial') && (
          <>
            <Title level={5} style={{ margin: '16px 0 12px' }}>Фазовые цели света</Title>
            <Space direction="vertical" size={8} css={commonStyles.fullWidth}>
              {ANNUAL_LIGHT_PHASES.map((phase) => (
                <Row gutter={16} key={phase} align="middle">
                  <Col span={6}>
                    <Text strong>{LIGHT_PHASE_LABELS[phase]}</Text>
                  </Col>
                  <Col span={6}>
                    <Form.Item name={['lightRequirement', 'phaseTargets', phase, 'dliMinMol']} label="DLI мин.">
                      <InputNumber style={{ width: '100%' }} min={0} step={0.5} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name={['lightRequirement', 'phaseTargets', phase, 'dliTargetMol']} label="DLI цель">
                      <InputNumber style={{ width: '100%' }} min={0} step={0.5} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={['lightRequirement', 'phaseTargets', phase, 'photoperiodTargetHours']}
                      label="Фотопериод (ч)"
                    >
                      <InputNumber style={{ width: '100%' }} min={0} max={24} step={0.5} />
                    </Form.Item>
                  </Col>
                </Row>
              ))}
            </Space>
          </>
        )}
        {selectedPlantType === 'perennial' && (
          <Alert
            style={{ marginTop: 12 }}
            type="warning"
            showIcon
            message="Для многолетних культур лучше задавать только общий базовый DLI/фотопериод или ранние этапы, без излишне детализированных фаз, если для культуры нет устойчивой агрономической нормы."
          />
        )}
      </div>

      {/* Care targets */}
      <div css={styles.formSection}>
        <Title level={5} style={{ margin: '0 0 12px' }}>Целевые коридоры ухода</Title>
        <Title level={5} style={{ margin: '0 0 12px', fontSize: 14 }}>Влажность субстрата</Title>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name={['careTargets', 'watering', 'triggerPct']} label="Триггер полива (%)">
              <InputNumber style={{ width: '100%' }} min={1} max={95} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={['careTargets', 'watering', 'targetPct']} label="Целевая влажность (%)">
              <InputNumber style={{ width: '100%' }} min={5} max={100} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={['careTargets', 'watering', 'maxPct']} label="Верхний предел (%)">
              <InputNumber style={{ width: '100%' }} min={10} max={100} />
            </Form.Item>
          </Col>
        </Row>
        <Title level={5} style={{ margin: '8px 0 12px', fontSize: 14 }}>Питание и pH</Title>
        {(['nitrogen', 'phosphorus', 'potassium'] as const).map((nutrient) => (
          <Row gutter={16} key={nutrient}>
            <Col span={4}>
              <Text strong>{nutrient === 'nitrogen' ? 'N' : nutrient === 'phosphorus' ? 'P' : 'K'}</Text>
            </Col>
            <Col span={10}>
              <Form.Item name={['careTargets', 'fertilizing', 'targets', nutrient, 'trigger']} label="Триггер">
                <InputNumber style={{ width: '100%' }} min={0} max={100} />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name={['careTargets', 'fertilizing', 'targets', nutrient, 'target']} label="Цель">
                <InputNumber style={{ width: '100%' }} min={0} max={100} />
              </Form.Item>
            </Col>
          </Row>
        ))}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name={['careTargets', 'fertilizing', 'pH', 'min']} label="pH мин.">
              <InputNumber style={{ width: '100%' }} min={3} max={10} step={0.1} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={['careTargets', 'fertilizing', 'pH', 'optimal']} label="pH цель">
              <InputNumber style={{ width: '100%' }} min={3} max={10} step={0.1} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={['careTargets', 'fertilizing', 'pH', 'max']} label="pH макс.">
              <InputNumber style={{ width: '100%' }} min={3} max={10} step={0.1} />
            </Form.Item>
          </Col>
        </Row>
      </div>

      {/* Yield model */}
      <div css={styles.formSection}>
        <Title level={5} style={{ margin: '0 0 12px' }}>Модель урожая (для овощных/плодовых)</Title>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name={['yieldModel', 'expectedYieldPerPlantKg']} label="Потенциал урожая (кг/раст)">
              <InputNumber style={{ width: '100%' }} min={0} step={0.1} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={['yieldModel', 'marketableFraction']} label="Товарная доля (0-1)">
              <InputNumber style={{ width: '100%' }} min={0} max={1} step={0.05} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={['yieldModel', 'declineRatePctPer100Edu']} label="Спад / 100 EDU (%)">
              <InputNumber style={{ width: '100%' }} min={1} max={80} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name={['yieldModel', 'rampUpEdu']} label="Разгон (EDU)">
              <InputNumber style={{ width: '100%' }} min={1} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name={['yieldModel', 'peakFruitingEdu']} label="Пик (EDU)">
              <InputNumber style={{ width: '100%' }} min={1} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name={['yieldModel', 'maxFruitingEdu']} label="Макс. плодоношение (EDU)">
              <InputNumber style={{ width: '100%' }} min={1} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name={['yieldModel', 'senescenceTailEdu']} label="Хвост старения (EDU)">
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
        </Row>
      </div>

      {/* Phenology */}
      <div css={styles.formSection}>
        <Title level={5} style={{ margin: '0 0 12px' }}>Фенология (GDD ≥10°C)</Title>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name={['phenology', 'dormancyEndGDD']} label="Конец покоя">
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name={['phenology', 'budSwellingGDD']} label="Набухание почек">
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name={['phenology', 'activeGrowthGDD']} label="Активный рост">
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name={['phenology', 'winterPrepGDD']} label="Подг. к зиме">
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
        </Row>
      </div>

      {/* Mortality */}
      <div css={styles.formSection}>
        <Title level={5} style={{ margin: '0 0 12px' }}>Пороги смертности</Title>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name={['mortality', 'droughtDaysLethal']} label="Засуха (дней)">
              <InputNumber style={{ width: '100%' }} min={1} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={['mortality', 'frostLethalC']} label="Мороз (°C)">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={['mortality', 'healthScoreLethal']} label="Мин. здоровье (0-100)">
              <InputNumber style={{ width: '100%' }} min={0} max={100} />
            </Form.Item>
          </Col>
        </Row>
      </div>
    </Form>
  );

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: 'Администрирование' },
            { title: 'Техкарты' },
          ]}
        />
      }
    >
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        <Card>
          <div css={styles.headerRow}>
            <Space direction="vertical" size={4}>
              <Title level={4} css={styles.titleNoMargin}>
                Техкарты — профили выращивания
              </Title>
              <Paragraph type="secondary" css={styles.paragraphNoMargin}>
                Инструкции по выращиванию растений: оптимальные режимы (температура, полив, свет),
                параметры роста, графики ухода и модель урожая. Ключевой компонент точности симуляции.
              </Paragraph>
            </Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreate}
            >
              Создать техкарту
            </Button>
          </div>
        </Card>

        <Card>
          <Space css={commonStyles.fullWidth} align="center" wrap>
            <Select
              value={plantTypeFilter}
              onChange={setPlantTypeFilter}
              css={styles.selectWidth220}
              options={[
                { value: 'all', label: 'Все типы растений' },
                ...Object.entries(PLANT_TYPE_LABELS).map(([value, label]) => ({ value, label })),
              ]}
            />
            <Input.Search
              placeholder="Поиск по названию или виду"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              css={styles.searchMaxWidth}
            />
          </Space>
        </Card>

        {loading && <Spin size="large" />}
        {isError && <Card><Empty description="Ошибка загрузки техкарт" /></Card>}

        {!loading && !isError && filtered.length === 0 && (
          <Card>
            <Empty description="Техкарты не найдены" />
          </Card>
        )}

        <Space direction="vertical" size={12} css={commonStyles.fullWidth}>
          {filtered.map((profile) => (
            <CultivationCard
              key={profile.id}
              profile={profile}
              onEdit={openEdit}
              onDelete={handleDelete}
            />
          ))}
        </Space>
      </Space>

      {/* Create Modal */}
      <Modal
        title="Создать техкарту"
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={handleCreate}
        confirmLoading={creating}
        okText="Создать"
        cancelText="Отмена"
        width={800}
        destroyOnClose
      >
        {renderFormContent()}
      </Modal>

      {/* Edit Modal */}
      <Modal
        title={`Редактировать: ${editingProfile?.profileName ?? ''}`}
        open={!!editingProfile}
        onCancel={() => setEditingProfile(null)}
        onOk={handleEdit}
        confirmLoading={updating}
        okText="Сохранить"
        cancelText="Отмена"
        width={800}
        destroyOnClose
      >
        {renderFormContent()}
      </Modal>
    </MainLayout>
  );
};

export default AdminCultivationCardsPage;
